/**
 * A production's status as the tags it actually is.
 *
 * The grids write status as a comma separated list: "DevTal, Development,
 * Active, Actively Casting". It was drawn as one badge holding the whole
 * string, which in a table column is one badge with an ellipsis in it. 1v1
 * said on 26 September that they could not read what was in it and that if
 * there are several they should be several tags, which is also what the
 * source means.
 *
 * Commas are the separator, and whitespace is collapsed before the label is
 * used: a handful of the live values carry a newline inside a tag, from a
 * grid cell that wrapped, so "Long Term Tracking -\nFeatures" and "Long Term
 * Tracking - Features" are the same tag written twice.
 */
export function statusTags(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((part) => part.replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

/**
 * How a production's type is written on screen.
 *
 * The API returns the enum as it is stored, which is "feature" and
 * "television". Those are the database's words, and 1v1 asked on 26 September
 * for a column saying "feature or TV", so the two the live data actually
 * carries are named and anything else is passed through as it arrives. The
 * fixtures carry longer labels like "Limited Series" and "Straight to Series",
 * which are already readable and should not be rewritten into one of these
 * two.
 */
const TYPE_LABELS: Record<string, string> = {
  feature: "Feature",
  television: "TV",
};

/**
 * The enum's third value, which means the grid said something the ingest
 * could not place. On screen that is the same as nothing being said, so it
 * reads as the empty case rather than putting the word "unknown" in a column
 * next to two real answers.
 */
const TYPE_UNKNOWN = "unknown";

export function typeLabel(type: string | null | undefined): string | null {
  if (!type) return null;
  const key = type.trim().toLowerCase();
  if (key === TYPE_UNKNOWN) return null;
  return TYPE_LABELS[key] ?? type;
}
